import { Component, Input, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';

import { CustomEventService } from '@shared/services/custom-event.service';
import { WidgetService } from '@shared/services/widget.service';

import ActionTypesEnum from '@shared/enums/action-constant.enum';
import { WidgetStatePlaceholderEnum } from '@shared/enums/widget-state-placeholder.enum';

import colorMap from '@shared/variables/chart-color.variable';
import { commonChartConfig, commonChartTooltip } from '@shared/variables/echart-common.variable';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { fromJS, Map } from 'immutable';

@UntilDestroy()
@Component({
  selector: 'app-jenkins-widget',
  templateUrl: './jenkins-widget.component.html'
})
export class JenkinsWidgetComponent implements OnInit {

  @Input() dateFilterable: boolean;
  widgetId: string;
  widgetName: string;
  widgetType: string;
  widgetFooter: Widget.FooterModel;
  headerAddon: string;
  headerComponents: [] = [];
  selectedDate: string;
  note: string;
  url: string;
  from: number;
  showDownloadIcon: boolean;

  @Input()
  set period(period: string) {
    if (period === 'FROM') {
      this.selectedDate = this.from.toString(10);
    } else if (period === 'TODAY') {
      this.selectedDate = `${period}`;
    } else if (period) {
      this.selectedDate = `FROM_LAST_${period}`;
    }
  };

  loading: boolean;
  initialLoading: boolean;
  widgetStatePlaceholderEnum = WidgetStatePlaceholderEnum;
  showWidgetStatePlaceholder: WidgetStatePlaceholderEnum;

  baseChartOptions: Map<string, any>;
  chartOptionMap: Map<string, any> = Map();
  echartsColors: string[];
  jenkinsData: Map<string, any> = Map();

  constructor(
    private customEventService: CustomEventService,
    private widgetService: WidgetService,
  ) { }

  ngOnInit(): void {
    this.loadWidgetData(true);

    // Subscribe to the dashboard announcements to update
    this.customEventService.eventAnnounced$.pipe(
      untilDestroyed(this)
    ).subscribe(({ action, data }) => {
      if (action === ActionTypesEnum.UPDATE_WIDGET_DATA) {
        this.loadWidgetData();
      }

      if (action === ActionTypesEnum.UPDATE_WIDGET_DATE) {
        const { id, date } = data;

        if (this.widgetId === id ) {
          this.selectedDate = date;
          this.loadWidgetData();
        }
      }
    });
  }

  loadWidgetData(initial?: boolean): void {
    this.loading = true;

    if (initial) {
      this.initialLoading = true;
    }

    this.widgetService.get(this.widgetId, this.selectedDate)
      .pipe(untilDestroyed(this), take(1))
      .subscribe(response => {
        this.loading = false;
        this.initialLoading = false;

        if (!response.data) {
        // || (
        //     (response.data.lastEvents && !response.data.lastEvents.length)
        //     && (response.data.fixTime && !response.data.fixTime.length)
        //     && (response.data.buildTime && !response.data.buildTime.length)
        //   )
          this.showWidgetStatePlaceholder = this.widgetStatePlaceholderEnum.DATA_NOT_FOUND;
          return;
        }
        this.showWidgetStatePlaceholder = undefined;

        if (response.footer) {
          this.widgetFooter = response.footer;
        }

        response.data.lastBuildTime = response.data.chartData.summary.displayData.pop();

        this.jenkinsData = fromJS(response.data);

        this.echartsColors = colorMap.get('short');

        // base chart options
        this.baseChartOptions = Map({
          grid: {
            top: 20,
            bottom: 10,
            left: 20,
            right: 20
          },
          backgroundColor: commonChartConfig.bg,
          color: this.echartsColors,
          tooltip: {
            ...commonChartTooltip
          },
          xAxis: {
            show: false,
            data: []
          },
          yAxis: {
            show: false
          }
        });

        const colorMapper = (colors) => ({
          normal: {
            color: params => colors[params.dataIndex]
          }
        });

        const jenkinsChartTooltipCreator = (elements: string[]): string => {
          const returnVal: string[] = [];

          elements.forEach(item => {
            if (item) {
              returnVal.push(item);
            }
          });
          return returnVal.join('</br>');
        };

        // sets each charts data to the Map in JS format
        this.jenkinsData.get('chartData').forEach((value, key) => {
          const tooltip = Map({
            tooltip: {
              formatter: (params) => jenkinsChartTooltipCreator([
                this.jenkinsData.getIn(['chartData', key, 'displayData', params.dataIndex]),
                this.jenkinsData.getIn(['chartData', key, 'date', params.dataIndex]),
                this.jenkinsData.getIn(['chartData', key, 'triggers', params.dataIndex])
              ])
            }
          });

          this.chartOptionMap = this.chartOptionMap
            .set(key, this.baseChartOptions
              .mergeDeep(Map({
                series: [{
                  data: this.jenkinsData.getIn(['chartData', key, 'data']),
                  type: this.jenkinsData.getIn(['chartData', key, 'type']),
                  itemStyle: colorMapper(this.jenkinsData.getIn(['chartData', key, 'colors']).toArray()),
                  barWidth: 10
                }]
              }))
              .mergeDeep(tooltip)
              .updateIn(['grid', 'left'], _ =>
                20 + (16 * (10 - this.jenkinsData.getIn(['chartData', key, 'data']).size))
              ).toJS());
        });
      }, error => {
        console.log('error while fetching widget data', error);
        this.loading = false;
        this.initialLoading = false;
        this.showWidgetStatePlaceholder = this.widgetStatePlaceholderEnum.DATA_NOT_FOUND;
      });
  }

  onChartEvent($event, key): void {
    window.open(this.jenkinsData.getIn(['chartData', key, 'url', $event.dataIndex]), '_blank');
  }
}
